import { createContext } from 'react';

export interface GroundSettings {
  /** ElevenLabs API key for high-quality TTS. */
  elevenLabsApiKey: string;
  /** ElevenLabs voice ID to narrate sessions. */
  elevenLabsVoiceId: string;
  /** Nostr Wallet Connect connection string (for real zaps/payments in production). */
  nwcConnectionString: string;
  /** When true, all Lightning activity and paid features are simulated locally. */
  demoMode: boolean;
}

export interface GroundStats {
  /** Practice sessions completed (protocol sessions + Foundations days). */
  sessions: number;
  /** Digital sovereignty roadmap items checked off. */
  steps: number;
  /** Sats received (simulated in demo mode). */
  satsEarned: number;
  /** Number of zaps sent. */
  zapsSent: number;
  /** Total sats sent via zaps. */
  satsSent: number;
}

export interface GroundIdentity {
  pubkey: string;
  npub: string;
  /** True when using the local, no-login demo key. */
  isGuest: boolean;
}

export interface GroundContextType {
  settings: GroundSettings;
  updateSettings: (patch: Partial<GroundSettings>) => void;

  stats: GroundStats;
  addSession: () => void;
  addZapSent: (sats: number) => void;
  addSatsEarned: (sats: number) => void;
  resetStats: () => void;

  identity: GroundIdentity;

  /** IDs of roadmap items that have been checked off. */
  checkedItems: string[];
  /** Toggle an item, returning its new checked state. */
  toggleItem: (id: string) => boolean;
  resetRoadmap: () => void;

  /** Locally-tracked zap totals per event id. */
  zapsByEvent: Record<string, number>;
  recordZap: (eventId: string, sats: number) => void;

  // --- Foundations (free 5-day protocol) ---

  /** IDs of completed micro-sections. */
  completedSections: string[];
  /** Mark a micro-section complete (idempotent). */
  completeSection: (id: string) => void;
  /** Days (1-5) that have been fully completed. */
  completedDays: number[];
  isDayComplete: (day: number) => boolean;
  /** True once Day 5 is complete — unlocks Protocols. */
  foundationsComplete: boolean;
  /** Journal entries keyed by day number (as a string). */
  journals: Record<string, string>;
  saveJournal: (day: number, text: string) => void;
  /** Mark a day complete (idempotent). */
  completeDay: (day: number) => void;
  resetFoundations: () => void;

  // --- Protocols (paid) ---

  /** Purchased protocol ids. */
  purchases: string[];
  /** Record a purchase (idempotent). */
  purchase: (id: string) => void;
  /** True when the protocol is owned directly or via the bundle. */
  ownsProtocol: (id: string) => boolean;
  resetPurchases: () => void;
}

export const GroundContext = createContext<GroundContextType | undefined>(undefined);
